import {AttrsSchema, Component, jsx} from 'panel';
import {VNode} from 'snabbdom';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {ChartType} from 'common/report/constants';
import {RetentionTrendsMetricChartEl} from 'common/report/retention/charts/retention-trends-metric-chart';
import {RetentionTrendsChartEl} from 'common/report/retention/charts/retention-trends-chart';
import {flattenQueryResponse} from 'common/report/retention/util/request';
import {getFormattedRowData, hasRetentionData} from 'common/report/retention/util/parsing';
import {RetentionCurveChartEl} from 'common/report/retention/charts/retention-curve-chart';
import {Popover} from 'common/charts/visualization/line-chart/types';
import {CurrentProject} from 'common/report/model-resources/projects';
import {RetentionQuery} from 'common/report/retention/models/retention-query';
import {SuccessResponse} from 'common/report/retention/models/types';
import {ReportLayoutManager} from 'common/report/contexts/report-layout';
import {FormattedRow} from 'common/report/retention/util/types';
import {AGGREGATED_ROW_NAME} from 'common/report/retention/util/constants';
import {
  frequencyLineChartDescription,
  getEventDisplayName,
  retentionLineChartDescription,
} from 'common/report/retention/util';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';

import {handlePopoverAction, RetentionPopover, ViewUsersType} from '../popover';
import {popoverBody} from '../popoverBody';
import css from './index.inline.styl';

interface LineChartPopoverState {
  colIdx: number;
  rowIdx: number;
  color: string;
  selectedRow: FormattedRow;
  popover: RetentionPopover;
}

type RetentionChartDisplayState = {
  lineChartPopoverState: null | LineChartPopoverState;
};

type RetentionQueryAndResults = {
  query: RetentionQuery;
  results: SuccessResponse;
};

type RetentionChartDisplayJsx = {
  props: {
    project: null | CurrentProject;
    queryAndResults: RetentionQueryAndResults;
  };
  attrs: {
    'is-download-mode': boolean;
    'show-resizing-layout': boolean;
  };
  on?: EmptyObj;
};

export class RetentionChartDisplay extends Component<
  RetentionChartDisplayState,
  RetentionChartDisplayJsx['attrs'],
  unknown,
  unknown,
  ChartsContextRegistry
> {
  static get attrsSchema(): AttrsSchema<RetentionChartDisplayJsx['attrs']> {
    return {
      'is-download-mode': `boolean`,
      'show-resizing-layout': `boolean`,
    };
  }

  get config() {
    return {
      css,
      useShadowDom: true,
      template: () => this.renderTemplate(),
      contexts: [`reportLayoutManager` as const],
      defaultState: {
        lineChartPopoverState: null,
      },
    };
  }

  private _project: null | CurrentProject = null;
  get project() {
    return this._project;
  }
  set project(project: null | CurrentProject) {
    this._project = project;
    this.update();
  }

  private query: RetentionQuery = null;
  private results: SuccessResponse;

  set queryAndResults(value: RetentionQueryAndResults) {
    this.query = value.query;
    this.results = value.results;
    this.update();
  }

  popooverCallback = (segmentName: string) => {
    if (segmentName === OTHER_SEGMENT_VALUE) {
      return [];
    }
    return [
      {
        label: `View retained users`,
        icon: `team`,
        value: ViewUsersType.Retain,
      },
      {
        label: `View dropped off users`,
        icon: `team`,
        value: ViewUsersType.DropOff,
      },
    ];
  };

  createPopover(selectedRow, colIdx, rowIdx) {
    return new RetentionPopover(
      {
        bornEventName: this.query.bornEvent && getEventDisplayName(this.query.bornEvent),
        bucketSizes: this.query.bucketSizes,
        dataGroupName: this.query.getDataGroupName(),
        dataGroupNamePlural: this.query.getDataGroupName({plural: true}),
        eventName: getEventDisplayName(this.query.event),
        groupBy: this.query.groupBy,
        retentionUnit: this.query.retentionUnit,
        dateUnit: this.query.dateUnit,
        dateRange: this.query.dateRange,
        retentionType: this.query.retentionType,
        unbounded: this.query.unbounded,
        projectTimezone: this.project?.timezone,
        query: this.query,
      },
      {
        absoluteValue: selectedRow.data[colIdx].formatted_value,
        absoluteDropOffValue: selectedRow.data[colIdx].absolute_drop_off_value,
        cohortSize: selectedRow.data[colIdx].cohort_size,
        columnIdx: this.query.chartType === ChartType.RetentionTrend ? this.query.retentionBucket : colIdx, // bucket index i.e. < 1 Day, Day 1, Day 2, ...
        rowIdx,
        ...(this.query.chartType === ChartType.RetentionTrend && {groupedRowIdx: colIdx}), // interval index i.e. Aug 15, 2022, Aug 22, 2022, ...
        completeDate: selectedRow.data[colIdx].complete_date,
        enteredValue: selectedRow.data[colIdx].entered_value,
        enteredDate: selectedRow.data[colIdx].date,
        incomplete: selectedRow.data[colIdx].incomplete,
        isAggregated:
          this.query.isSegmented() || selectedRow.raw_head === AGGREGATED_ROW_NAME(this.query.retentionType),
      },
    );
  }

  private renderTemplate(): VNode {
    return (
      <div sel='.root' class={{'resizing-layout': this.attr(`show-resizing-layout`)}}>
        {this.renderChart()}
      </div>
    );
  }

  private renderChart(): null | VNode {
    if (this.query.chartType === ChartType.RetentionTrendMetric) {
      return this.renderMetricChart();
    }

    if (this.query.chartType === ChartType.RetentionTrend) {
      return this.renderTrendChart();
    }

    const {metadata, data} = flattenQueryResponse(this.results, this.query.groupBy);
    const tableData = getFormattedRowData(data, metadata, this.query.groupBy, this.project?.timezone);
    if (this.query.isRetentionCurveBasedChart() && hasRetentionData(tableData)) {
      return this.renderCurveChart();
    }
    return null;
  }

  private renderMetricChart(): VNode {
    return (
      <div sel='.chart-widget.metric-chart-container'>
        <RetentionTrendsMetricChartEl
          sel='.retention-trends-metric-chart'
          class={{'fixed-size-chart': this.reportLayoutManager.hasFixedSizeChart()}}
          params={{
            projectTimezone: this.project?.timezone,
            paramsAndResults: {
              params: this.query.generateBookmark(),
              results: this.results,
            },
          }}
        />
      </div>
    );
  }

  private renderTrendChart(): VNode {
    return (
      <div
        sel='.chart-widget.trends-chart-container'
        className={{'fixed-size-chart': this.reportLayoutManager.hasFixedSizeChart()}}
      >
        <RetentionTrendsChartEl
          attrs={{
            'project-timezone': this.project?.timezone,
            'utc-offset': this.project?.utcOffset,
            'show-annotations': !this.attr(`is-download-mode`),
          }}
          props={{
            paramsAndResults: {
              params: this.query.generateBookmark(),
              results: this.results,
            },
            popoverCallback: this.popooverCallback,
          }}
          on={{
            popoverAction: (ev) => {
              const {action, selectedRow, colIdx, rowIdx, color} = ev.detail;
              const popover = this.createPopover(selectedRow, colIdx, rowIdx);
              handlePopoverAction({
                action,
                color,
                component: this,
                query: this.query,
                popover,
              });
            },
          }}
        />
      </div>
    );
  }

  private renderCurveChart(): VNode {
    const curveChartNote = this.query.isAddiction()
      ? frequencyLineChartDescription(this.query)
      : retentionLineChartDescription(this.query);

    return (
      <div sel='.curve-chart-container' class={{'fixed-size-chart': this.reportLayoutManager.hasFixedSizeChart()}}>
        <RetentionCurveChartEl
          sel='.chart-widget.retention-curve-chart'
          class={{'fixed-size-chart': this.reportLayoutManager.hasFixedSizeChart()}}
          attrs={{
            'project-timezone': this.project?.timezone,
            'utc-offset': this.project?.utcOffset,
            'show-popover': true,
          }}
          props={{
            paramsAndResults: {
              params: this.query.generateBookmark(),
              results: this.results,
            },
            popover: (): Popover => {
              return {
                body: () => {
                  const {colIdx, selectedRow, popover} = this.state.lineChartPopoverState;
                  const PopOverBody = popoverBody({
                    isAggregate: popover.isAggregatedCell(),
                    retainedCount: popover.getRetainedCount(),
                    dataGroupName: popover.getDataGroupName(),
                    dataGroupNamePlural: popover.getDataGroupNamePlural(),
                    retainedPercent: selectedRow.data[colIdx].formatted_percent,
                    dropOffCount: popover.getDropOffCount(),
                    dropOffPercent: selectedRow.data[colIdx].percent_drop_off_value,
                  });
                  return PopOverBody;
                },
                attrs: () => {
                  return {
                    title: this.state.lineChartPopoverState.popover.getTitle(),
                    subtitle: this.state.lineChartPopoverState.popover.getSubtitle(),
                    'button-options': this.state.lineChartPopoverState.popover.getButtonOptions(),
                    'prevent-close-selectors': [`.bar.hoverable`],
                  };
                },
                eventHandlers: () => {
                  return {
                    action: (ev) =>
                      handlePopoverAction({
                        action: ev.detail.action,
                        color: this.state.lineChartPopoverState.color,
                        component: this,
                        query: this.query,
                        popover: this.state.lineChartPopoverState.popover,
                      }),
                  };
                },
                maxWidth: 300,
              };
            },
          }}
          on={{
            lineChartClick: (ev: CustomEvent) => {
              const {rowIdx, colIdx, color, selectedRow} = ev.detail;
              const popover = this.createPopover(selectedRow, colIdx, rowIdx);
              this.update({
                lineChartPopoverState: {color, colIdx, rowIdx, selectedRow, popover},
              });
            },
          }}
        />
        <div sel='.retention-line-chart-footnote'>{curveChartNote}</div>
      </div>
    );
  }

  private get reportLayoutManager(): ReportLayoutManager {
    return this.getContext(`reportLayoutManager`);
  }
}

export const RetentionChartDisplayEl = defineAndMakeJsxFactory<RetentionChartDisplayJsx>(
  `retention-chart-display`,
  RetentionChartDisplay,
);
export const RetentionChartDisplayReact = panel2react<RetentionChartDisplayJsx>(
  `retention-chart-display`,
  RetentionChartDisplay,
);
